import { lazy, Suspense } from "react";
import type { FC, ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { reportQueryError } from "@/lib/sentry";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { trpc, trpcClient } from "@/lib/trpc";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { RequireRole } from "@/components/layout/RequireRole";
import { MfaEnrollment, MfaReauth } from "@/components/prospection/MfaEnrollment";
import { FREELANCE_ONBOARDING_PATH, isFreelanceRole, landingPathForRole, type PageKey } from "@/lib/rbac";
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Companies = lazy(() => import("./pages/Companies"));
const Tenders = lazy(() => import("./pages/Tenders"));
const GoQueue = lazy(() => import("./pages/GoQueue"));
const Partners = lazy(() => import("./pages/Partners"));
const Projects = lazy(() => import("./pages/Projects"));
const Billing = lazy(() => import("./pages/Billing"));
const Messages = lazy(() => import("./pages/Messages"));
const Support = lazy(() => import("./pages/Support"));
const PrivateProjects = lazy(() => import("./pages/PrivateProjects"));
const OpsNoShows = lazy(() => import("./pages/OpsNoShows"));
const OpsQuickReports = lazy(() => import("./pages/OpsQuickReports"));
const OpsCallQueue = lazy(() => import("./pages/OpsCallQueue"));
const OpsDuplicates = lazy(() => import("./pages/OpsDuplicates"));
const OpsInactiveArtisans = lazy(() => import("./pages/OpsInactiveArtisans"));
const OpsTags = lazy(() => import("./pages/OpsTags"));
const OpsBulkGift = lazy(() => import("./pages/OpsBulkGift"));
const SalesPipeline = lazy(() => import("./pages/SalesPipeline"));
const SalesLeads = lazy(() => import("./pages/SalesLeads"));
const SalesLeadDetail = lazy(() => import("./pages/SalesLeadDetail"));
const SalesPerformance = lazy(() => import("./pages/SalesPerformance"));
const SalesForecast = lazy(() => import("./pages/SalesForecast"));
const SalesActivities = lazy(() => import("./pages/SalesActivities"));
const OnboardingPipeline = lazy(() => import("./pages/OnboardingPipeline"));
const ArtisansModeration = lazy(() => import("./pages/ArtisansModeration"));
const ArtisansAnnuaire = lazy(() => import("./pages/ArtisansAnnuaire"));
const ArtisanDetail = lazy(() => import("./pages/ArtisanDetail"));
const TravauxBTPReferrals = lazy(() => import("./pages/TravauxBTPReferrals"));
const TravauxBTPDashboard = lazy(() => import("./pages/TravauxBTPDashboard"));
const ParticulierDetail = lazy(() => import("./pages/ParticulierDetail"));
const CompanyDetail = lazy(() => import("./pages/CompanyDetail"));
const ChurnRisk = lazy(() => import("./pages/ChurnRisk"));
const LpLeads = lazy(() => import("./pages/LpLeads"));
const AcquisitionVivier = lazy(() => import("./pages/AcquisitionVivier"));
const AcquisitionWarmup = lazy(() => import("./pages/AcquisitionWarmup"));
const AcquisitionSettings = lazy(() => import("./pages/AcquisitionSettings"));
const AcquisitionCopilot = lazy(() => import("./pages/AcquisitionCopilot"));
const AcquisitionInbox = lazy(() => import("./pages/AcquisitionInbox"));
const AcquisitionSequences = lazy(() => import("./pages/AcquisitionSequences"));
const AcquisitionCompanyDetail = lazy(() => import("./pages/AcquisitionCompanyDetail"));
const AcquisitionDashboard = lazy(() => import("./pages/AcquisitionDashboard"));
const AcquisitionCalls = lazy(() => import("./pages/AcquisitionCalls"));
const AcquisitionSequenceDetail = lazy(() => import("./pages/AcquisitionSequenceDetail"));
const Disputes = lazy(() => import("./pages/Disputes"));
const DisputeDetail = lazy(() => import("./pages/DisputeDetail"));
const ConversionPipeline = lazy(() => import("./pages/ConversionPipeline"));
const Transactions = lazy(() => import("./pages/Transactions"));
const ReviewsModeration = lazy(() => import("./pages/ReviewsModeration"));
const SLALeads = lazy(() => import("./pages/SLALeads"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const TrialAlerts = lazy(() => import("./pages/TrialAlerts"));
const MessageModeration = lazy(() => import("./pages/MessageModeration"));
const Search = lazy(() => import("./pages/Search"));
const AuditLog = lazy(() => import("./pages/AuditLog"));
const BroadcastPage = lazy(() => import("./pages/BroadcastPage"));
const MarketplaceStats = lazy(() => import("./pages/MarketplaceStats"));
const AcquisitionFunnel = lazy(() => import("./pages/AcquisitionFunnel"));
const Refunds = lazy(() => import("./pages/Refunds"));
const FraudAlerts = lazy(() => import("./pages/FraudAlerts"));
const MarketplaceGeo = lazy(() => import("./pages/MarketplaceGeo"));
const ParticulierQuality = lazy(() => import("./pages/ParticulierQuality"));
const DocumentsExpiration = lazy(() => import("./pages/DocumentsExpiration"));
const EInvoicing = lazy(() => import("./pages/EInvoicing"));
const PlansSaas = lazy(() => import("./pages/PlansSaas"));
const SeoOverview = lazy(() => import("./pages/seo/SeoOverview"));
const SeoPages = lazy(() => import("./pages/seo/SeoPages"));
const SeoPlanner = lazy(() => import("./pages/seo/SeoPlanner"));
const SeoIssues = lazy(() => import("./pages/seo/SeoIssues"));
const SeoSettings = lazy(() => import("./pages/seo/SeoSettings"));
const ContentDashboard = lazy(() => import("./pages/content/ContentDashboard"));
const ContentAgent = lazy(() => import("./pages/content/ContentAgent"));
const ContentPlanning = lazy(() => import("./pages/content/ContentPlanning"));
const ContentLibrary = lazy(() => import("./pages/content/ContentLibrary"));
const ContentDrafts = lazy(() => import("./pages/content/ContentDrafts"));
const ContentHistory = lazy(() => import("./pages/content/ContentHistory"));
const ContentEditor = lazy(() => import("./pages/content/ContentEditor"));
const ContentSettings = lazy(() => import("./pages/content/ContentSettings"));
const ContentSeoOpportunities = lazy(() => import("./pages/content/ContentSeoOpportunities"));
const Netlinking = lazy(() => import("./pages/Netlinking"));
// Lot A — espace prospection freelances.
const AcceptInvite = lazy(() => import("./pages/prospection/AcceptInvite"));
// Mot de passe oublié (routes publiques) + « Mon compte » — tout compte staff.
const ForgotPassword = lazy(() => import("./pages/account/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/account/ResetPassword"));
const MyAccount = lazy(() => import("./pages/account/MyAccount"));
const ProspectionCalls = lazy(() => import("./pages/prospection/ProspectionCalls"));
const ProspectionCompanies = lazy(() => import("./pages/prospection/ProspectionCompanies"));
const ProspectionCompanyDetail = lazy(() => import("./pages/prospection/ProspectionCompanyDetail"));
const ProspectionSdrMeetings = lazy(() => import("./pages/prospection/ProspectionSdrMeetings"));
const ProspectionSdrStats = lazy(() => import("./pages/prospection/ProspectionSdrStats"));
const ProspectionSlots = lazy(() => import("./pages/prospection/ProspectionSlots"));
const ProspectionSalesMeetings = lazy(() => import("./pages/prospection/ProspectionSalesMeetings"));
const ProspectionPipeline = lazy(() => import("./pages/prospection/ProspectionPipeline"));
const ProspectionLeadDetail = lazy(() => import("./pages/prospection/ProspectionLeadDetail"));
const ProspectionSalesStats = lazy(() => import("./pages/prospection/ProspectionSalesStats"));
const ProspectionManage = lazy(() => import("./pages/prospection/ProspectionManage"));
// Lot B — gains, conversions, commissions.
const ProspectionEarnings = lazy(() => import("./pages/prospection/ProspectionEarnings"));
const ProspectionConversions = lazy(() => import("./pages/prospection/ProspectionConversions"));
// Espace freelance pro (05/10/2026).
const ProspectionDashboard = lazy(() => import("./pages/prospection/ProspectionDashboard"));
const ProspectionOnboarding = lazy(() => import("./pages/prospection/ProspectionOnboarding"));
const ProspectionPayouts = lazy(() => import("./pages/prospection/ProspectionPayouts"));
const ProspectionNotifications = lazy(() => import("./pages/prospection/ProspectionNotifications"));
const ProspectionCommissions = lazy(() => import("./pages/prospection/ProspectionCommissions"));
import { NotificationListener } from "./components/NotificationListener";
import { UpdateBanner } from "./components/UpdateBanner";

const queryClient = new QueryClient({
  // Central Sentry capture for every query/mutation failure (skips expected
  // control-flow HTTP errors). Replaces the need to instrument each catch.
  queryCache: new QueryCache({
    onError: (error, query) => reportQueryError(error, { queryKey: query.queryKey }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      reportQueryError(error, { mutationKey: mutation.options.mutationKey }),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Lot A : un freelance doit activer la double authentification avant tout
  // (le serveur ne lui répond rien d'autre tant qu'elle n'est pas activée).
  if (user?.mfaRequired && !user.totpEnabled) {
    return <MfaEnrollment />;
  }
  // Session ouverte sans second facteur (avant l'enrôlement, ou ancienne) :
  // le serveur refuse l'espace prospection — reconnexion avec le code.
  if (user?.mfaRequired && !user.mfaSession) {
    return <MfaReauth />;
  }
  // Espace freelance pro : dossier d'accueil (entreprise, RIB, contrat)
  // obligatoire avant tout le reste (le serveur le refuse de toute façon).
  if (user?.freelanceOnboardingDone === false && location.pathname !== FREELANCE_ONBOARDING_PATH && location.pathname !== "/mon-compte") {
    return <Navigate to={FREELANCE_ONBOARDING_PATH} replace />;
  }

  return <>{children}</>;
}

/**
 * « / » = Dashboard pour le staff interne ; un freelance n'a pas de Dashboard
 * et atterrit directement sur son outil principal.
 */
function HomeRoute() {
  const { user } = useAuth();
  if (isFreelanceRole(user?.staffRole)) {
    return <Navigate to={landingPathForRole(user?.staffRole)} replace />;
  }
  return (
    <RequireRole pageKey="DASHBOARD">
      <Dashboard />
    </RequireRole>
  );
}

/** Route protégée + garde de page — raccourci des routes de l'espace prospection. */
function guarded(pageKey: PageKey, node: React.ReactNode) {
  return (
    <ProtectedRoute>
      <RequireRole pageKey={pageKey}>{node}</RequireRole>
    </ProtectedRoute>
  );
}

const routeFallback = (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={routeFallback}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
      <Route path="/mon-compte" element={guarded("MY_ACCOUNT", <MyAccount />)} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomeRoute />
          </ProtectedRoute>
        }
      />
      {/* /users removed — staff management is in Admin app */}
      <Route path="/users" element={<Navigate to="/companies" replace />} />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <Search />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="COMPANIES">
              <Companies />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="COMPANIES">
              <CompanyDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenders"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TENDERS">
              <Tenders />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/go-queue"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="GO_QUEUE">
              <GoQueue />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PARTNERS">
              <Partners />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PROJECTS">
              <Projects />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="BILLING">
              <Billing />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* Categories, Audit migrated to Admin app */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SUPPORT">
              <Support />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MESSAGES">
              <Messages />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* ApiKeys, Pricing, SystemSettings, SystemAnalytics migrated to Admin app */}
      <Route
        path="/private-projects"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <PrivateProjects />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-no-shows"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <OpsNoShows />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-quick-reports"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <OpsQuickReports />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-call-queue"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <OpsCallQueue />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-duplicates"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsDuplicates />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-inactive"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsInactiveArtisans />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-tags"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsTags />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ops-bulk-gift"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <OpsBulkGift />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* Sales routes */}
      <Route
        path="/sales/pipeline"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_PIPELINE">
              <SalesPipeline />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/leads"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_LEADS">
              <SalesLeads />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/leads/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_LEADS">
              <SalesLeadDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/performance"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_PERFORMANCE">
              <SalesPerformance />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/forecast"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_FORECAST">
              <SalesForecast />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/activities"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SALES_ACTIVITIES">
              <SalesActivities />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ONBOARDING">
              <OnboardingPipeline />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversion-pipeline"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONVERSION_PIPELINE">
              <ConversionPipeline />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRAVAUXBTP_DASHBOARD">
              <TravauxBTPDashboard />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/artisans"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <ArtisansModeration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/artisans/all"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <ArtisansAnnuaire />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/artisans/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ARTISANS_MODERATION">
              <ArtisanDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/reviews"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="REVIEWS_MODERATION">
              <ReviewsModeration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/documents"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="DOCUMENTS_EXPIRATION">
              <DocumentsExpiration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/einvoicing"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="EINVOICING">
              <EInvoicing />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/travauxbtp/referrals"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRAVAUXBTP_REFERRALS">
              <TravauxBTPReferrals />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/particuliers/:projectId"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PRIVATE_PROJECTS">
              <ParticulierDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* FeatureFlags migrated to Admin app */}
      <Route
        path="/churn-risk"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CHURN_RISK">
              <ChurnRisk />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lp-leads"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="LP_LEADS">
              <LpLeads />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-vivier"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_VIVIER">
              <AcquisitionVivier />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-sequences"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_SEQUENCES">
              <AcquisitionSequences />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-warmup"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_WARMUP">
              <AcquisitionWarmup />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-copilot"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_AI">
              <AcquisitionCopilot />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-settings"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_SETTINGS">
              <AcquisitionSettings />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-inbox"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_INBOX">
              <AcquisitionInbox />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-dashboard"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_DASHBOARD">
              <AcquisitionDashboard />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-calls"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_CALLS">
              <AcquisitionCalls />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-vivier/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_VIVIER">
              <AcquisitionCompanyDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-sequences/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_SEQUENCES">
              <AcquisitionSequenceDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRANSACTIONS">
              <Transactions />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sla-leads"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SLA_LEADS">
              <SLALeads />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SUBSCRIPTIONS">
              <Subscriptions />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trial-alerts"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="TRIAL_ALERTS">
              <TrialAlerts />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messagerie-moderation"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MESSAGE_MODERATION">
              <MessageModeration />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/disputes"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="DISPUTES">
              <Disputes />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/disputes/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="DISPUTES">
              <DisputeDetail />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/refunds"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="REFUNDS">
              <Refunds />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace-stats"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MARKETPLACE_STATS">
              <MarketplaceStats />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace-geo"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="MARKETPLACE_GEO">
              <MarketplaceGeo />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisition-funnel"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="ACQUISITION_FUNNEL">
              <AcquisitionFunnel />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fraud-alerts"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="FRAUD_ALERTS">
              <FraudAlerts />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/broadcast"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="BROADCAST">
              <BroadcastPage />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="AUDIT_LOG">
              <AuditLog />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/particulier-quality"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PARTICULIER_QUALITY">
              <ParticulierQuality />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans-saas"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="PLANS_SAAS">
              <PlansSaas />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SEO">
              <SeoOverview />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/pages"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SEO">
              <SeoPages />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/planner"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SEO">
              <SeoPlanner />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/issues"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SEO">
              <SeoIssues />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/settings"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="SEO">
              <SeoSettings />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* ═══ Contenu (module éditorial) ═══ */}
      <Route
        path="/contenu"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentDashboard />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contenu/agent"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentAgent />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contenu/planning"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentPlanning />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contenu/bibliotheque"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentLibrary />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contenu/brouillons"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentDrafts />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contenu/historique"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentHistory />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* Détail d'un contenu — cible des liens du planning, de la bibliothèque,
          des brouillons et de l'historique. Sans cette route, tout le module
          renverrait sur la redirection `*` et l'éditeur serait inatteignable. */}
      <Route
        path="/contenu/opportunites"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentSeoOpportunities />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* Netlinking : UNE seule page (objectifs, opportunités, échanges, liens, réglages). */}
      <Route
        path="/netlinking"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="NETLINKING">
              <Netlinking />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contenu/reglages"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentSettings />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contenu/item/:id"
        element={
          <ProtectedRoute>
            <RequireRole pageKey="CONTENT">
              <ContentEditor />
            </RequireRole>
          </ProtectedRoute>
        }
      />
      {/* ── Espace prospection freelances (Lot A) ── */}
      {/* Activation d'un compte freelance : route PUBLIQUE (jeton d'invitation). */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/prospection/appels" element={guarded("PROSPECTION_CALLS", <ProspectionCalls />)} />
      <Route path="/prospection/prospects" element={guarded("PROSPECTION_COMPANIES", <ProspectionCompanies />)} />
      <Route path="/prospection/prospects/:id" element={guarded("PROSPECTION_COMPANIES", <ProspectionCompanyDetail />)} />
      <Route path="/prospection/mes-rdv" element={guarded("PROSPECTION_SDR_MEETINGS", <ProspectionSdrMeetings />)} />
      <Route path="/prospection/stats-sdr" element={guarded("PROSPECTION_SDR_STATS", <ProspectionSdrStats />)} />
      <Route path="/prospection/disponibilites" element={guarded("PROSPECTION_SLOTS", <ProspectionSlots />)} />
      <Route path="/prospection/rdv" element={guarded("PROSPECTION_SALES_MEETINGS", <ProspectionSalesMeetings />)} />
      <Route path="/prospection/pipeline" element={guarded("PROSPECTION_PIPELINE", <ProspectionPipeline />)} />
      <Route path="/prospection/pipeline/:id" element={guarded("PROSPECTION_PIPELINE", <ProspectionLeadDetail />)} />
      <Route path="/prospection/stats" element={guarded("PROSPECTION_SALES_STATS", <ProspectionSalesStats />)} />
      <Route path="/prospection/pilotage" element={guarded("PROSPECTION_MANAGE", <ProspectionManage />)} />
      <Route path="/prospection/gains" element={guarded("PROSPECTION_EARNINGS", <ProspectionEarnings />)} />
      <Route path="/prospection/conversions" element={guarded("PROSPECTION_CONVERSIONS", <ProspectionConversions />)} />
      <Route path="/prospection/tableau-de-bord" element={guarded("PROSPECTION_DASHBOARD", <ProspectionDashboard />)} />
      <Route path="/prospection/bienvenue" element={guarded("PROSPECTION_ONBOARDING", <ProspectionOnboarding />)} />
      <Route path="/prospection/versements" element={guarded("PROSPECTION_PAYOUTS", <ProspectionPayouts />)} />
      <Route path="/prospection/notifications" element={guarded("PROSPECTION_NOTIFICATIONS", <ProspectionNotifications />)} />
      <Route path="/prospection/commissions" element={guarded("PROSPECTION_COMMISSIONS", <ProspectionCommissions />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

// Conflit de versions @types/react (19.x exige `children` sur ReactPortal) rend
// le type de `trpc.Provider` (tRPC v10) invalide en JSX. Cast local, sans effet
// runtime, vers un composant typé.
const TrpcProvider = trpc.Provider as unknown as FC<{
  client: typeof trpcClient;
  queryClient: typeof queryClient;
  children: ReactNode;
}>;

const App = () => (
  <TrpcProvider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <UpdateBanner />
            <NotificationListener />
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </TrpcProvider>
);

export default App;

